import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Star, TrendUp, TrendDown, UsersThree, SoccerBall } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { logger } from "@/lib/logger";

const fmt = (n) => (Number(n) || 0).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
const fmtNum = (n) => (Number(n) || 0).toLocaleString("es-ES");
const TOOLTIP = { background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: 6, color: "#0F172A", fontSize: 12 };

export default function BalanceAnual() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/balance/annual");
        setRows(data.seasons || []);
      } catch (e) {
        logger.warn("Balance load failed:", e?.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Ordenar cronológicamente ascendente para el gráfico
  const chartData = [...rows].reverse().map((r) => ({
    name: r.name,
    ingresos: r.ingresos,
    gastos: r.gastos,
    beneficio: r.beneficio,
  }));

  const totalIng = rows.reduce((a, r) => a + r.ingresos, 0);
  const totalGas = rows.reduce((a, r) => a + r.gastos, 0);
  const totalBenef = totalIng - totalGas;
  const totalAsist = rows.reduce((a, r) => a + r.asistencia, 0);

  return (
    <div className="space-y-6" data-testid="balance-page">
      <div>
        <div className="text-[11px] tracking-[0.25em] uppercase text-[#2563EB] font-bold">Comparativa</div>
        <h1 className="font-display font-bold text-4xl sm:text-5xl uppercase tracking-tighter">Balance Anual</h1>
        <p className="text-sm text-slate-500 mt-1">Comparativa entre temporadas · Ingresos · Gastos · Asistencia</p>
      </div>

      {/* Totales globales */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi label="Ingresos histórico" value={fmt(totalIng)} color="#16a34a" icon={TrendUp} />
        <Kpi label="Gastos histórico" value={fmt(totalGas)} color="#dc2626" icon={TrendDown} />
        <Kpi label="Beneficio acumulado" value={fmt(totalBenef)} color={totalBenef >= 0 ? "#16a34a" : "#dc2626"} icon={totalBenef >= 0 ? TrendUp : TrendDown} />
        <Kpi label="Asistencia total" value={fmtNum(totalAsist)} color="#0A2D5C" icon={UsersThree} />
      </div>

      {/* Gráfico comparativo */}
      <div className="bg-white border border-slate-200 p-5 shadow-card" style={{ borderRadius: 12 }}>
        <h3 className="font-display font-bold text-lg uppercase tracking-tight mb-3">Ingresos vs Gastos por temporada</h3>
        <div className="h-72">
          {chartData.length === 0 ? (
            <div className="h-full flex items-center justify-center text-slate-400 text-sm">Sin datos aún</div>
          ) : (
            <ResponsiveContainer>
              <BarChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="name" axisLine={false} tickLine={false} />
                <YAxis axisLine={false} tickLine={false} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k€`} />
                <Tooltip contentStyle={TOOLTIP} formatter={(v) => fmt(v)} />
                <Legend />
                <Bar dataKey="ingresos" fill="#22C55E" radius={[6, 6, 0, 0]} />
                <Bar dataKey="gastos" fill="#EF4444" radius={[6, 6, 0, 0]} />
                <Bar dataKey="beneficio" fill="#2563EB" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Tabla comparativa */}
      <div className="bg-white border border-slate-200 shadow-card overflow-x-auto" style={{ borderRadius: 12 }}>
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500">
              <th className="px-4 py-3 font-bold">Temporada</th>
              <th className="px-4 py-3 font-bold">Estado</th>
              <th className="px-4 py-3 font-bold text-right">Partidos</th>
              <th className="px-4 py-3 font-bold text-right">Asistencia</th>
              <th className="px-4 py-3 font-bold text-right">Ingresos</th>
              <th className="px-4 py-3 font-bold text-right">Gastos</th>
              <th className="px-4 py-3 font-bold text-right">Beneficio</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-500">Cargando…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-500">Sin temporadas creadas todavía</td></tr>
            )}
            {rows.map((r) => {
              const positivo = r.beneficio >= 0;
              return (
                <tr key={r.id} className={`border-b border-slate-100 ${r.is_current ? "bg-emerald-50/40" : ""}`} data-testid={`balance-row-${r.name}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {r.is_current && <Star size={14} weight="fill" className="text-emerald-500" />}
                      <span className="font-display font-bold uppercase tracking-tight">{r.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 capitalize text-slate-600">{r.status}</td>
                  <td className="px-4 py-3 text-right font-display font-bold flex items-center justify-end gap-1"><SoccerBall size={13} className="text-slate-400" />{r.matches}</td>
                  <td className="px-4 py-3 text-right font-display font-bold">{fmtNum(r.asistencia)}</td>
                  <td className="px-4 py-3 text-right font-display font-bold text-emerald-600">{fmt(r.ingresos)}</td>
                  <td className="px-4 py-3 text-right font-display font-bold text-red-600">{fmt(r.gastos)}</td>
                  <td className={`px-4 py-3 text-right font-display font-black ${positivo ? "text-emerald-700" : "text-red-700"}`}>{fmt(r.beneficio)}</td>
                </tr>
              );
            })}
          </tbody>
          {rows.length > 0 && (
            <tfoot className="bg-slate-50 border-t-2 border-slate-300">
              <tr>
                <td className="px-4 py-3 font-display font-bold uppercase text-[11px] tracking-widest text-slate-600" colSpan={3}>Total histórico</td>
                <td className="px-4 py-3 text-right font-display font-black">{fmtNum(totalAsist)}</td>
                <td className="px-4 py-3 text-right font-display font-black text-emerald-700">{fmt(totalIng)}</td>
                <td className="px-4 py-3 text-right font-display font-black text-red-700">{fmt(totalGas)}</td>
                <td className={`px-4 py-3 text-right font-display font-black ${totalBenef >= 0 ? "text-emerald-700" : "text-red-700"}`}>{fmt(totalBenef)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

function Kpi({ label, value, color, icon: Icon }) {
  return (
    <div className="bg-white border border-slate-200 p-4 shadow-card" style={{ borderRadius: 12 }}>
      <div className="flex items-center gap-2">
        <Icon size={14} style={{ color }} />
        <div className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-bold">{label}</div>
      </div>
      <div className="font-display font-bold text-2xl tracking-tighter mt-1" style={{ color }}>{value}</div>
    </div>
  );
}
